/** @jsx jsx */
/** @jsxRuntime classic */

import React from "react";
import { jsx } from "@emotion/core";
import classNames from "classnames";
import { ColumnDetail } from "helpers/use-table";
import { TableInfo } from "api";
import { useDetectOverflow } from "helpers/useDetectOverflow";
import { useSelector } from "react-redux";
import { selectCollapse } from "reducers/leftMenu";
import Sort from "assets/Icon_SortBy_01.png";
import SortASC from "assets/Icon_SortBy_03.png";
import SortDESC from "assets/Icon_SortBy_02.png";
import OptionsButton from "./ui/OptionsButton";

import "styles/DataTable.scss";
import { Cell } from "helpers/tables/Cell";

function makeRow<T extends { [K: string]: React.ReactNode }>(
  r: T,
  id: number | string,
  columns: ColumnDetail<T>[],
  formatPrimitives: (v: unknown) => unknown,
  hiddenColumns: string[],
  formatRow?: (r: T) => { [K in keyof T]: React.ReactNode },
  customColWidth?: { column: number; width: React.CSSProperties },
  wrapFirstCol?: boolean
): React.ReactNode {
  const row = formatRow ? formatRow(r) : r;
  const columnKeys = columns.map(c => c.columnKey.toString().toLowerCase());
  const findOrderNumber = (key: keyof T) => {
    const col = columns.find(
      x => x.columnKey.toString().toLowerCase() === key.toString().toLowerCase()
    );
    if (!col) return 0;
    return col.orderNumber;
  };
  const pairs = Object.entries(row)
    .filter(([k]) => {
      if (columnKeys.includes(k.toLowerCase()) && !hiddenColumns.includes(k)) {
        return true;
      }

      return false;
    })
    .map(([k, v]) => [k, formatPrimitives(v)] as [keyof T, React.ReactNode])
    .sort((a, b) => {
      const aOrder = findOrderNumber(a[0]);
      const bOrder = findOrderNumber(b[0]);
      if (aOrder === bOrder) return 0;
      return aOrder < bOrder ? -1 : 1;
    }) as [keyof T, React.ReactNode][];

  const rowData = pairs.map(p => p[1]);

  return <Row key={id} row={rowData} customColWidth={customColWidth} wrapFirstCol={wrapFirstCol && wrapFirstCol} />;
}

type PrimitiveFormatters = Partial<{
  boolean(b: boolean): React.ReactNode;
  string(s: string): React.ReactNode;
  number(n: number): React.ReactNode;
}>;

const defaultPrimitiveFormatters: PrimitiveFormatters = {
  boolean: (b: boolean) => (b ? "Yes" : "No"),
};

const Head: React.FC<{
  notSortable?: boolean;
  table: TableInfo;
  columns: ColumnDetail<any>[];
  toggleColumnSort(key: string): void;
  hiddenColumns: string[];
  headerColour?: string;
  customColWidth?: { column: number; width: React.CSSProperties };
}> = ({
  columns,
  toggleColumnSort,
  hiddenColumns,
  table,
  notSortable,
  headerColour,
  customColWidth,
}) => {
  const columnsFormatted = columns
    .filter(
      x =>
        x.bVisible &&
        !hiddenColumns
          .map(c => c.toLowerCase())
          .includes(String(x.columnKey).toLowerCase())
    )
    .sort((a, b) => (a.orderNumber > b.orderNumber ? 1 : -1))
    .map((column, i) => {
      const sortByValue = table.data?.details.sortBy[String(column.columnKey)];
      let src = Sort;
      if (sortByValue === "ASC") src = SortASC;
      if (sortByValue === "DESC") src = SortDESC;
      return (
        <Cell
          backgroundColour={headerColour}
          style={
            i === customColWidth?.column ? customColWidth.width : undefined
          }
          key={String(column.columnKey)}
        >
          {!notSortable && column.bSortable ? (
            <button
              onClick={() => toggleColumnSort(String(column.columnKey))}
              className="Sort"
              title={column.labelValue}
            >
              <span>{column.labelValue}</span>
              <img src={src} alt="Sort" />
            </button>
          ) : (
            <div className="Sort" title={column.labelValue}>
              <span>{column.labelValue}</span>
            </div>
          )}
        </Cell>
      );
    });

  return (
    <React.Fragment>
      <div id="StickySpot"></div>
      <div id="MainTableHeader" className="MainTableHeader">
        {[...columnsFormatted]}
      </div>
    </React.Fragment>
  );
};

const Row: React.FC<{
  row: React.ReactNode[];
  customColWidth?: { column: number; width: React.CSSProperties };
  wrapFirstCol?: boolean;
}> = ({ row, customColWidth, wrapFirstCol }) => {
  return (
    <div className="Row">
      {row.map((r, i) => {
        if (i === 0) {
          return (
            <Cell
              key={i}
              style={
                i === customColWidth?.column ? customColWidth.width : undefined
              }
            >
              <span style={{ whiteSpace: wrapFirstCol ? "normal" : "nowrap" }}>{r}</span>
            </Cell>
          );
        }
        if (i === row.length - 1) {
          return (
            <Cell
              key={i}
              style={
                i === customColWidth?.column ? customColWidth.width : undefined
              }
            >
              <span>{r}</span>
            </Cell>
          );
        }
        return (
          <Cell
            key={i}
            style={
              i === customColWidth?.column ? customColWidth.width : undefined
            }
          >
            <span>{r}</span>
          </Cell>
        );
      })}
    </div>
  );
};

export function OptionRowTable(props: {
  table: TableInfo;
  visibleRowOptionListener?(id: number | null): void;
  visibleOptionsMenu: number | null;
  setVisibleOptionsMenu(id: number | null): void;
  userName?: string | null;
  setUserName?(name: string | null): void;
  children(data: any): React.ReactNode;
  headerColour?: string;
  dashboardTable?: boolean;
}): JSX.Element | null {
  const {
    table,
    visibleRowOptionListener,
    visibleOptionsMenu,
    setVisibleOptionsMenu,
    userName,
    setUserName,
    dashboardTable,
    children,
  } = props;

  const headerColour = undefined;
  const [overflow] = useDetectOverflow("MainTable");
  //const menuCollapsed = useSelector(selectCollapse);
  if (!table.data?.details) return null;
  const { listData } = table.data.details;

  return (
    <div className={overflow ? "OptionsColumn BoxShadow" : "OptionsColumn"}>
      <div className="OptionsColumnHeader">
        <Cell backgroundColour={headerColour} />
      </div>
      <div className="OptionsColumnBody">
        {listData.map((row, i) => (
          <div className="Row" key={i}>
            <Cell>
              <OptionsButton
                config={row}
                visibleRowOptionListener={visibleRowOptionListener}
                visibleOptionsMenu={visibleOptionsMenu}
                setVisibleOptionsMenu={setVisibleOptionsMenu}
                userName={userName ? userName : ""}
                setUserName={setUserName}
                dashboardOptionsButton={dashboardTable}
              >
                {children(row)}
              </OptionsButton>
            </Cell>
          </div>
        ))}
      </div>
    </div>
  );
}

export type TableProps<T> = {
  table: TableInfo;
  primitiveFormatters?: PrimitiveFormatters;
  rowFormatter?(r: T): { [K in keyof T]: React.ReactNode };
  hiddenColumns?: string[];
  notSortable?: boolean;
  rowOptionComponent?: React.ReactNode;
  visibleOptionsMenu: number | null;
  setVisibleOptionsMenu(id: number | null): void;
  dashboardTable?: boolean;
  title?: string;
  href?: string | null;
  icon?: any;
  headerColour?: string;
  customColWidth?: { column: number; width: React.CSSProperties };
  wrapFirstCol?: boolean;
};

function DataTable<T>({
  table,
  primitiveFormatters,
  rowFormatter,
  hiddenColumns,
  notSortable,
  rowOptionComponent,
  visibleOptionsMenu,
  setVisibleOptionsMenu,
  dashboardTable,
  title,
  href,
  icon,
  customColWidth,
  wrapFirstCol,
}: TableProps<T>) {
  const headerColour = undefined;
  const { loading, error, data } = table;
  const [overflow] = useDetectOverflow("MainTable");
  const menuCollapsed = useSelector(selectCollapse);
  const classnames = classNames(
    "MainTable",
    { NoOptions: !rowOptionComponent },
    { BoxShadow: overflow },
    { DashboardTable: dashboardTable }
  );
  const wrapperClassnames = classNames(
    "TableWrapper",
    { DashboardTable: dashboardTable },
    { MenuCollapsed: menuCollapsed }
  );

  function formatPrimitives(value: unknown) {
    const formatter = { ...defaultPrimitiveFormatters, ...primitiveFormatters };

    if (typeof value === "string" && formatter.string)
      return formatter.string(value);
    if (typeof value === "number" && formatter.number)
      return formatter.number(value);
    if (typeof value === "boolean" && formatter.boolean)
      return formatter.boolean(value);

    return value;
  }
  if (loading && data == null) return <h2>Loading...</h2>;
  if (error) return <h2>Error!</h2>;

  if (data && data.details) {
    const rows =
      data.details.listData.map((l, i) =>
        makeRow<any>(
          l,
          i,
          data.details.columns,
          formatPrimitives,
          hiddenColumns ?? [],
          rowFormatter,
          customColWidth,
          wrapFirstCol,
        )
      ) ?? [];

    return (
      <React.Fragment>
        <div className={wrapperClassnames}>
          <div className="Inner">
            {title &&
              (href ? (
                <div className="TableTitle">
                  <h5>
                    <a href={href}>
                      <span>
                        {icon && (
                          <img className="TableIcon" src={icon} alt="Icon" />
                        )}
                      </span>
                      {title}
                    </a>
                  </h5>
                </div>
              ) : (
                <div className="TableTitle">
                  <h5>
                    <span>
                      {icon && (
                        <img className="TableIcon" src={icon} alt="Icon" />
                      )}
                    </span>
                    {title}
                  </h5>
                </div>
              ))}
            <div className="TableContainer">
              <div id="MainTable" className={classnames}>
                <Head
                  headerColour={headerColour}
                  notSortable={notSortable}
                  table={table}
                  columns={data.details.columns}
                  toggleColumnSort={data.actions.toggleColumnSort}
                  hiddenColumns={hiddenColumns ?? []}
                  customColWidth={customColWidth}
                />
                <div className="MainTableBody">{rows}</div>
              </div>
              {rowOptionComponent && rowOptionComponent}
            </div>
          </div>
        </div>
      </React.Fragment>
    );
  }

  return null;
}

export default React.memo<any>(DataTable);
